import React, { useContext } from "react";
import { createBrowserRouter, Outlet, RouterProvider, Navigate } from "react-router-dom";
import { AuthContext, AuthProvider } from "./context/authContext";
import { ErrorProvider, useError } from "./context/errorContext";
import { getBranding } from "./hooks/tennant";

import Home from "./pages/home/home";
import Calendar from "./pages/calendar/calendar";
import MapScreen from "./pages/mapScreen/mapScreen";
import Timeline from "./pages/mapScreen/timeline/timeline";
import Data from "./pages/data/data";
import Users from "./pages/data/users/users";
import Team from "./pages/data/team/team";
import Fuhrpark from "./pages/data/fuhrpark/fuhrpark";
import Tracker from "./pages/data/tracker/tracker";
import Tennants from "./pages/data/tennants/tennants";
import Roles from "./pages/data/roles/roles";
import ApiKeys from "./pages/data/apiKeys/apiKeys";
import Customer from "./pages/customer/customer";
import DeliveryNotes from "./pages/deliveryNotes/deliveryNotes";
import Bills from "./pages/bills/bills";
import Navbar from "./components/navbar/navbar";
import NavbarMobile from "./components/navbarMobile/navbarMobile";
import Print from "./pages/print/print";
import List from "./pages/list/list";
import Operator from "./pages/operator/operator";
import OperatorView from "./pages/operator/operatorView/operatorView";
import Settings from "./pages/settings/settings";
import Sign from "./pages/sign/sign";
import Login from "./pages/login/login";
import Unauthorized from "./pages/unauthorized/unauthorized";
import Offline from "./pages/offline/offline";
import NotFound from "./pages/notfound/notfound";

import ErrorMessage from "./components/errorMessage/errorMessage";

import moment from 'moment';
import 'moment/locale/de';
moment.locale('de');


// Service Worker registrieren
if ('serviceWorker' in navigator && 'PushManager' in window) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/serviceWorker.js')
  });
}

const branding = await getBranding();

document.documentElement.style.setProperty("--accent", branding.accentColor);

function PrivateRoute({ children, requiredPermission }) {
  const { user, isLoading, hasPermission } = useContext(AuthContext);

  if (isLoading) {
    return <div className="loading" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (
    requiredPermission &&
    !hasPermission(requiredPermission.resource, requiredPermission.action)
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
}

function RedirectBasedOnRole() {
  const { user, isLoading } = useContext(AuthContext);

  if (isLoading) return <div className="loading" />;

  if (!user) return <Navigate to="/login" replace />;

  return <Navigate to={user.landingPage} replace />;
}

function Layout() {

  const { error } = useError();

  return (
    <div className="site">
      <Navbar />
      <Outlet />
      <NavbarMobile />
      <ErrorMessage errorMessage={error} />
    </div>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        element: <RedirectBasedOnRole />,
      },
      {
        path: "/",
        element: (
          <PrivateRoute requiredPermission={{ resource: "planner", action: "read" }}>
            <Home />
          </PrivateRoute>
        ),
        children: [
          {
            path: "calendar",
            element: (
              <PrivateRoute>
                <Calendar />
              </PrivateRoute>
            ),
          },
          {
            path: "list",
            element: (
              <PrivateRoute>
                <List />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: "/data",
        element: (
          <PrivateRoute requiredPermission={{ resource: "data", action: "read" }}>
            <Data />
          </PrivateRoute>
        ),
        children: [
          {
            path: "",
            element: <Navigate to="/data/items" replace />,
          },
          {
            path: "/data/users",
            element: (
              <PrivateRoute requiredPermission={{ resource: "user", action: "read" }}>
                <Users />
              </PrivateRoute>
            ),
          },
          {
            path: "/data/items",
            element: (
              <PrivateRoute>
                <Fuhrpark />
              </PrivateRoute>
            ),
          },
          {
            path: "/data/team",
            element: (
              <PrivateRoute>
                <Team />
              </PrivateRoute>
            ),
          },
          {
            path: "/data/tracker",
            element: (
              <PrivateRoute>
                <Tracker />
              </PrivateRoute>
            ),
          },
          {
            path: "/data/tennants",
            element: (
              <PrivateRoute requiredPermission={{ resource: "user", action: "read" }}>
                <Tennants />
              </PrivateRoute>
            ),
          },
          {
            path: "/data/roles",
            element: (
              <PrivateRoute requiredPermission={{ resource: "user", action: "read" }}>
                <Roles />
              </PrivateRoute>
            ),
          },
          {
            path: "/data/apikey",
            element: (
              <PrivateRoute requiredPermission={{ resource: "user", action: "read" }}>
                <ApiKeys />
              </PrivateRoute>
            ),
          },
        ],
      },
      {
        path: "/customer",
        element: (
          <PrivateRoute>
            <Customer />
          </PrivateRoute>
        ),
      },
      {
        path: "/deliverynotes",
        element: (
          <PrivateRoute>
            <DeliveryNotes />
          </PrivateRoute>
        ),
      },
      {
        path: "/bills",
        element: (
          <PrivateRoute>
            <Bills />
          </PrivateRoute>
        ),
      },
      {
        path: "/settings",
        element: (
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        ),
      },
      {
        path: "/map",
        element: (
          <PrivateRoute requiredPermission={{ resource: "map", action: "read" }}>
            <MapScreen />
          </PrivateRoute>
        ),
      },
      {
        path: "/map/timeline/:id",
        element: (
          <PrivateRoute requiredPermission={{ resource: "timeline", action: "read" }}>
            <Timeline />
          </PrivateRoute>
        ),
      },
      {
        path: "/operator",
        element: (
          <PrivateRoute requiredPermission={{ resource: "operator", action: "read" }}>
            <Operator />
          </PrivateRoute>
        ),
      },
      {
        path: "/operator/event/:id",
        element: (
          <PrivateRoute requiredPermission={{ resource: "operator", action: "read" }}>
            <OperatorView />
          </PrivateRoute>
        ),

      },
      {
        path: "/unauthorized",
        element: <Unauthorized />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/mapWarter",
    element: (
      <PrivateRoute requiredPermission={{ resource: "warter", action: "read" }}>
        <MapScreen />
      </PrivateRoute>
    ),
  },
  {
    path: "/sign",
    element: (
      <PrivateRoute requiredPermission={{ resource: "sign", action: "write" }}>
        <Sign />
      </PrivateRoute>
    ),
  },
  {
    path: "/offline",
    element: <Offline />,
  },
  {
    path: "/print/:id",
    element: (
      <PrivateRoute>
        <Print />
      </PrivateRoute>
    ),
  },
]);

function App() {
  return (
    <AuthProvider>
      <ErrorProvider>
        <RouterProvider router={router} />
      </ErrorProvider>
    </AuthProvider>
  );
}

export default App;